import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  IProductFilter,
  IProductFilterOption,
  productFilterKeyType,
  productFiltersConstant,
} from '../constants/product/productFilter.constant';
import { changeProductFilterOptionIsSelected } from '../utils/filter/changeProductFilterOptionIsSelected.util';
import { resetProductFilterOptionIsSelected } from '../utils/filter/resetProductFilterOptionIsSelected.util';

type productFilterStateType = {
  isFilterOptionsAdded: {
    brands: boolean;
  };
  isSearchParamChecked: boolean;
  clickedFilterButtonIndex?: number;
  haveToRefreshLocalStates: boolean;
  globalProductFilters: IProductFilter[];
};

const initialState: productFilterStateType = {
  isFilterOptionsAdded: {
    brands: false,
  },
  isSearchParamChecked: false,
  haveToRefreshLocalStates: false,
  globalProductFilters: productFiltersConstant,
};

export const productFilterSlice = createSlice({
  name: 'productFilter',
  initialState,
  reducers: {
    // 필터 옵션의 추가 여부를 관리하는 Action
    changeIsFilterOptionsAdded: (state, action: PayloadAction<{ data: 'brands'; value: boolean }>) => {
      if (action.payload.data === 'brands') {
        state.isFilterOptionsAdded.brands = action.payload.value;
      }
    },

    // PLP 진입 시 달고 들어온 Search Param 이 체크되었는지의 여부를 관리하는 Action
    changeIsSearchParamChecked: (state, action: PayloadAction<boolean>) => {
      state.isSearchParamChecked = action.payload;
    },

    // PLP 에서 클릭된 필터 버튼의 index 를 변경하기 위한 Action
    changeClickedFilterButtonIndex: (state, action: PayloadAction<number | undefined>) => {
      state.clickedFilterButtonIndex = action.payload;
    },

    // 로컬 상태를 갱신해야하는지에 대한 여부를 관리하는 Action
    changeHaveToRefreshLocalStates: (state, action: PayloadAction<boolean>) => {
      state.haveToRefreshLocalStates = action.payload;
    },

    // 전체 필터의 isSelected 값을 초기화하는 Action (== 유저가 선택한 필터 값을 초기화)
    resetAllFilters: (state) => {
      const newProductFilters = resetProductFilterOptionIsSelected({ currentFilters: state.globalProductFilters });
      state.globalProductFilters = newProductFilters;
    },

    // 필터 1개의 isSelected 값을 초기화하는 Action (선택된 필터 옵션 중에서, radio 타입의 필터 옵션을 삭제할 때 사용, 해당 필터 옵션을 초기 값으로 되돌림)
    resetOneFilter: (state, action: PayloadAction<IProductFilter>) => {
      const newProductFilters = resetProductFilterOptionIsSelected({
        currentFilters: state.globalProductFilters,
        targetFilter: action.payload,
      });
      state.globalProductFilters = newProductFilters;
    },

    // 전체 필터 값을 변경할 때 사용 (지역 상태로 관리하던 필터 값을 전역 상태로 올릴 때 사용)
    changeFilters: (state, action: PayloadAction<IProductFilter[]>) => {
      state.globalProductFilters = action.payload;
    },

    // 필터 옵션을 추가할 때 사용하는 Action (서버 데이터를 기반으로 하는 필터 옵션을 추가할 때 사용 (ex) brands))
    addFilterOptions: (
      state,
      action: PayloadAction<{
        filterKey: productFilterKeyType;
        options: IProductFilterOption[];
      }>,
    ) => {
      const newProductFilters = state.globalProductFilters.map((filter) => {
        if (filter.key === action.payload.filterKey) {
          filter.options = action.payload.options;
        }
        return filter;
      });

      state.globalProductFilters = newProductFilters;
    },

    // 필터 옵션의 isSelected 값을 바꿀 때 사용하는 Action
    changeProductFilterOptionIsSelected: (
      state,
      action: PayloadAction<{
        targetFilter: IProductFilter;
        targetOption: IProductFilterOption;
      }>,
    ) => {
      const newProductFilters = changeProductFilterOptionIsSelected({
        currentFilters: state.globalProductFilters,
        targetFilter: action.payload.targetFilter,
        targetOption: action.payload.targetOption,
      });

      state.globalProductFilters = newProductFilters;
    },
  },
});
